/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { AlertCircle, Clock, CheckCircle2, PlusCircle } from 'lucide-react';
import { TablePlan, ReservationPlan } from './types.ts';
import { evaluerStatutTable } from './logiquePure.ts';

interface AlerteLibererProps {
  tables: TablePlan[];
  reservations: ReservationPlan[];
  heureAffichee: string;
  onProlonger: (reservationId: string, minutes: number) => Promise<void>;
  onTerminerReservation: (reservationId: string) => Promise<void>;
  onSelectionnerTable: (tableId: string) => void;
}

export const AlerteLiberer: React.FC<AlerteLibererProps> = ({
  tables,
  reservations,
  heureAffichee,
  onProlonger,
  onTerminerReservation,
  onSelectionnerTable,
}) => {
  // Trouver toutes les tables qui ont dépassé leur durée prévue
  const alertes: {
    table: TablePlan;
    reservation: ReservationPlan;
    retardMinutes: number;
  }[] = [];

  for (const table of tables) {
    const info = evaluerStatutTable(table, reservations, heureAffichee);
    if (info.statut === 'a_liberer' && info.reservationEnCours) {
      const tempsRestant = info.tempsRestantMinutes ?? 0;
      const retard = Math.abs(tempsRestant);
      alertes.push({
        table,
        reservation: info.reservationEnCours,
        retardMinutes: retard > 0 ? retard : 15,
      });
    }
  }

  if (alertes.length === 0) return null;

  return (
    <div
      className="bg-orange-500 text-slate-950 px-3 sm:px-4 py-2 border-b border-orange-600 flex flex-col gap-2 shadow-sm animate-pulse-subtle"
      role="alert"
      aria-live="assertive"
    >
      {alertes.map(({ table, reservation, retardMinutes }) => (
        <div
          key={table.id}
          className="flex flex-wrap items-center justify-between gap-2.5 text-xs sm:text-sm"
        >
          <div className="flex items-center gap-2">
            <span className="p-1 rounded-full bg-slate-950 text-orange-400">
              <AlertCircle className="w-4 h-4" />
            </span>
            <div>
              <span className="font-extrabold tracking-tight">
                Table {table.nom} à libérer
              </span>{' '}
              <span className="text-slate-900 font-medium">
                — {reservation.nom} ({reservation.couverts} pers., durée prévue dépassée de{' '}
                <strong className="underline">{retardMinutes} min</strong>)
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onSelectionnerTable(table.id)}
              aria-label={`Localiser la table ${table.nom} sur le plan`}
              className="px-2.5 py-1 rounded bg-orange-400 hover:bg-orange-300 font-semibold text-xs cursor-pointer min-h-[36px] flex items-center transition-colors"
            >
              Voir la table
            </button>

            <button
              type="button"
              onClick={() => onProlonger(reservation.id, 15)}
              aria-label={`Prolonger la table ${table.nom} de 15 minutes`}
              className="px-3 py-1 rounded bg-white hover:bg-slate-100 text-slate-950 font-bold text-xs flex items-center gap-1 cursor-pointer min-h-[36px] shadow-xs active:scale-95 transition-all"
            >
              <PlusCircle className="w-3.5 h-3.5 text-orange-600" />
              <span>Prolonger de 15 min</span>
            </button>

            <button
              type="button"
              onClick={() => onTerminerReservation(reservation.id)}
              aria-label={`Libérer la table ${table.nom} et terminer le repas`}
              className="px-3 py-1 rounded bg-slate-950 hover:bg-slate-900 text-white font-bold text-xs flex items-center gap-1 cursor-pointer min-h-[36px] shadow-xs active:scale-95 transition-all"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span>Terminé</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
